
import { SecurityLog } from "@/types/security";
import { 
  Table, 
  TableHeader, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell 
} from "@/components/ui/table";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { formatDistance } from "date-fns";
import { ptBR } from "date-fns/locale";

interface SecurityLogsTableProps {
  logs: SecurityLog[];
  isLoading: boolean;
}

export const SecurityLogsTable = ({ logs, isLoading }: SecurityLogsTableProps) => {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<{
    field: keyof SecurityLog;
    direction: "asc" | "desc";
  }>({
    field: "timestamp",
    direction: "desc"
  });
  
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(logs.length / pageSize));
  
  // Apply sorting and pagination
  const sortedLogs = [...logs].sort((a, b) => {
    const aValue = a[sortBy.field];
    const bValue = b[sortBy.field];
    
    if (sortBy.field === "timestamp") {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return sortBy.direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    
    if (aValue < bValue) return sortBy.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortBy.direction === "asc" ? 1 : -1;
    return 0;
  });
  
  const paginatedLogs = sortedLogs.slice((page - 1) * pageSize, page * pageSize);
  
  const handleSort = (field: keyof SecurityLog) => {
    setSortBy(prev => ({
      field,
      direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc"
    }));
  };
  
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return (
      <div>
        <div>{date.toLocaleDateString("pt-BR")}</div>
        <div className="text-xs text-muted-foreground">
          {date.toLocaleTimeString("pt-BR")}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatDistance(date, new Date(), { addSuffix: true, locale: ptBR })}
        </div>
      </div>
    );
  };
  
  const renderStatusBadge = (status: "success" | "failure") => {
    return (
      <Badge variant={status === "success" ? "success" : "destructive"}>
        {status === "success" ? "Sucesso" : "Falha"}
      </Badge>
    );
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (logs.length === 0) {
    return (
      <div className="text-center p-8 border rounded-lg bg-background">
        <p className="text-muted-foreground">Nenhum registro de log encontrado.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead 
                className="cursor-pointer"
                onClick={() => handleSort("timestamp")}
              >
                Data/Hora {sortBy.field === "timestamp" && (sortBy.direction === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead 
                className="cursor-pointer"
                onClick={() => handleSort("userId")}
              >
                Usuário {sortBy.field === "userId" && (sortBy.direction === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead 
                className="cursor-pointer"
                onClick={() => handleSort("actionType")}
              >
                Ação {sortBy.field === "actionType" && (sortBy.direction === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead 
                className="cursor-pointer"
                onClick={() => handleSort("resource")}
              >
                Recurso {sortBy.field === "resource" && (sortBy.direction === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead 
                className="cursor-pointer"
                onClick={() => handleSort("status")}
              >
                Status {sortBy.field === "status" && (sortBy.direction === "asc" ? "↑" : "↓")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedLogs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                <TableCell>{log.userName || log.userId}</TableCell>
                <TableCell>
                  <Badge variant="outline">{log.actionType}</Badge>
                </TableCell>
                <TableCell>{log.resource}</TableCell>
                <TableCell>{renderStatusBadge(log.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (page > 1) {
                    setPage(p => p - 1);
                  }
                }}
                aria-disabled={page === 1}
                className={page === 1 ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <PaginationItem key={p}>
                <PaginationLink
                  href="#"
                  isActive={page === p}
                  onClick={(e) => {
                    e.preventDefault();
                    setPage(p);
                  }}
                >
                  {p}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext 
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (page < totalPages) {
                    setPage(p => p + 1);
                  }
                }}
                aria-disabled={page === totalPages}
                className={page === totalPages ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};
